import { WsRpcGroup } from "@cmmd-forge/contracts";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schedule from "effect/Schedule";
import { RpcClient, RpcSerialization } from "effect/unstable/rpc";
import * as Socket from "effect/unstable/socket/Socket";

export const WS_RECONNECT_INITIAL_DELAY_MS = 1_000;
export const WS_RECONNECT_BACKOFF_FACTOR = 2;
export const WS_RECONNECT_MAX_DELAY_MS = 64_000;
export const WS_RECONNECT_MAX_RETRIES = 10;

export interface WsRpcProtocolLifecycleHandlers {
  readonly onAttempt?: (socketUrl: string) => void;
  readonly onOpen?: () => void;
  readonly onError?: (message: string) => void;
  readonly onClose?: (details: { readonly code: number; readonly reason: string }) => void;
}

export interface WsRpcProtocolOptions {
  readonly url: WsRpcProtocolSocketUrlProvider;
  readonly lifecycle?: WsRpcProtocolLifecycleHandlers;
  readonly maxRetries?: number;
  readonly delayForRetry?: (retryIndex: number) => number | null;
}

/**
 * Deliberately still the bare `WsRpcGroup`, not `SessionWsRpcGroup`.
 *
 * Building it from the guarded group would let this client decode
 * `SessionAuthorizationError` — on the bare group that refusal arrives as an
 * unknown tag and surfaces as a schema failure instead of the reason. It also
 * makes `WsRpcSession` structurally incompatible with `RelayRpcSession`, which
 * `apps/mobile`'s `MobileRuntimeRpcClient` relies on to accept either.
 *
 * Left alone because the refusal is unreachable from any shipped client: every
 * first-party sign-in path issues the `owner` role, and mobile — the one client
 * that connects on this lane with a possibly-`client` role — calls none of the
 * nine methods the policy restricts. Swap this to `SessionWsRpcGroup` and widen
 * the mobile session type together, when a `client`-role UI first needs to read
 * one of these refusals.
 */
export const makeWsRpcProtocolClient = RpcClient.make(WsRpcGroup);
type RpcClientFactory = typeof makeWsRpcProtocolClient;
export type WsRpcProtocolClient =
  RpcClientFactory extends Effect.Effect<infer Client, any, any> ? Client : never;
export type WsRpcProtocolSocketUrlProvider = string | (() => Promise<string>);

export function getWsReconnectDelayMsForRetry(retryIndex: number): number | null {
  if (!Number.isInteger(retryIndex) || retryIndex < 0 || retryIndex >= WS_RECONNECT_MAX_RETRIES) {
    return null;
  }
  return Math.min(
    Math.round(WS_RECONNECT_INITIAL_DELAY_MS * WS_RECONNECT_BACKOFF_FACTOR ** retryIndex),
    WS_RECONNECT_MAX_DELAY_MS,
  );
}

export function resolveWsRpcSocketUrl(rawUrl: string): string {
  const resolved = new URL(rawUrl);
  if (resolved.protocol !== "ws:" && resolved.protocol !== "wss:") {
    throw new Error(`Unsupported websocket transport URL protocol: ${resolved.protocol}`);
  }
  resolved.pathname = "/ws";
  return resolved.toString();
}

function formatSocketErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return String(error);
}

export function createWsRpcProtocolLayer(options: WsRpcProtocolOptions) {
  const lifecycle = options.lifecycle;
  const maxRetries = options.maxRetries ?? WS_RECONNECT_MAX_RETRIES;
  const delayForRetry = options.delayForRetry ?? getWsReconnectDelayMsForRetry;

  const resolvedUrl =
    typeof options.url === "function"
      ? Effect.promise(() => (options.url as () => Promise<string>)()).pipe(
          Effect.map((rawUrl) => resolveWsRpcSocketUrl(rawUrl)),
          Effect.tapError((error) =>
            Effect.sync(() => {
              lifecycle?.onError?.(formatSocketErrorMessage(error));
            }),
          ),
          Effect.orDie,
        )
      : resolveWsRpcSocketUrl(options.url);

  const webSocketConstructorLayer = Layer.succeed(
    Socket.WebSocketConstructor,
    (socketUrl, protocols) => {
      lifecycle?.onAttempt?.(socketUrl);
      const socket = new globalThis.WebSocket(socketUrl, protocols);

      socket.addEventListener(
        "open",
        () => {
          lifecycle?.onOpen?.();
        },
        { once: true },
      );
      socket.addEventListener(
        "error",
        () => {
          lifecycle?.onError?.("Unable to connect to the Forge server WebSocket.");
        },
        { once: true },
      );
      socket.addEventListener(
        "close",
        (event) => {
          lifecycle?.onClose?.({ code: event.code, reason: event.reason });
        },
        { once: true },
      );

      return socket;
    },
  );

  const socketLayer = Socket.layerWebSocket(resolvedUrl).pipe(
    Layer.provide(webSocketConstructorLayer),
  );

  const retryPolicy = Schedule.addDelay(Schedule.recurs(maxRetries), (retryCount) =>
    Effect.succeed(Duration.millis(delayForRetry(retryCount) ?? 0)),
  );

  const protocolLayer = Layer.effect(
    RpcClient.Protocol,
    RpcClient.makeProtocolSocket({
      retryPolicy,
      retryTransientErrors: true,
    }),
  );

  return protocolLayer.pipe(Layer.provide(Layer.mergeAll(socketLayer, RpcSerialization.layerJson)));
}
